import React, { useRef, useState, useEffect } from "react";
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import "./ContratoTab.css";

export default function ContratoTab({ paciente }) {
  const canvasRef = useRef(null);
  const [signaturePad, setSignaturePad] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSpec, setSelectedSpec] = useState("");
  const [availableSpecs, setAvailableSpecs] = useState([]);

  // 🧠 Extraer especialidades del paciente
  useEffect(() => {
    if (!paciente?.servicios) return;
    const serviciosArr = Array.isArray(paciente.servicios)
      ? paciente.servicios
      : String(paciente.servicios)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

    setAvailableSpecs(serviciosArr);
    if (serviciosArr.length === 1) {
      setSelectedSpec(serviciosArr[0]);
    }
  }, [paciente]);

  // 🖋️ Inicializar firma
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.width = 350;
    canvas.height = 150;

    const pad = new SignaturePad(canvas, {
      backgroundColor: "#fff",
      penColor: "#000",
    });
    pad.onEnd = () => setIsSigned(!pad.isEmpty());
    setSignaturePad(pad);

    return () => {
      pad.off();
      pad.clear();
    };
  }, [fileUrl]);

  // 🧾 Obtener contrato PDF (solo cuando se selecciona especialidad)
  useEffect(() => {
    if (!paciente?.id || !selectedSpec) return;

    setLoading(true);
    setError("");
    setFileUrl(null);
    setMessage("");

    const url = `https://te-ayudo.es/back/api/generate_contract.php?id=${paciente.id}&specialty=${selectedSpec}`;
    console.log("📄 Solicitando contrato desde:", url);

    fetch(url, { credentials: "include" })
      .then(async (res) => {
        const text = await res.text();
        console.log("📩 Respuesta cruda generate_contract:", text);
        try {
          return JSON.parse(text);
        } catch {
          throw new Error("Respuesta del backend no es JSON válido");
        }
      })
      .then((data) => {
        console.log("✅ JSON parseado (generate_contract):", data);
        if (data.ok) {
          const url = data.pdf_local || data.file_url;
          if (url) {
            setFileUrl(url);
          } else {
            throw new Error("No se recibió ninguna URL de contrato");
          }
        } else {
          throw new Error(data.error || "Error al generar contrato");
        }
      })
      .catch((err) => {
        console.error("💥 Error al obtener contrato:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [paciente?.id, selectedSpec]);

  // 🧹 Borrar firma
  const handleClear = () => {
    signaturePad?.clear();
    setIsSigned(false);
    console.log("🧹 Firma borrada");
  };

  // 🖋️ Guardar contrato firmado y subirlo
  const handleSave = async () => {
    console.log("▶️ handleSave ejecutado");

    if (!signaturePad || signaturePad.isEmpty()) {
      alert("Por favor, firma el contrato antes de guardar.");
      console.warn("❌ No se puede guardar: sin firma");
      return;
    }
    if (!fileUrl) {
      alert("No se ha podido cargar el contrato para firmar.");
      console.warn("❌ No se puede guardar: fileUrl vacío");
      return;
    }

    setUploading(true);
    setMessage("");
    console.log("📤 Subiendo contrato firmado a backend...");

    try {
      const existingPdfBytes = await fetch(fileUrl).then((res) =>
        res.arrayBuffer()
      );
      console.log("📄 PDF original cargado correctamente.");

      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];
      const { width } = lastPage.getSize();

      // 🔹 Capturar la firma
      const signatureDataUrl = signaturePad.toDataURL();
      const pngBytes = await fetch(signatureDataUrl).then((res) =>
        res.arrayBuffer()
      );
      const pngImage = await pdfDoc.embedPng(pngBytes);

      lastPage.drawImage(pngImage, {
        x: (width - 200) / 2,
        y: 80,
        width: 200,
        height: 80,
      });

      console.log("✍️ Firma añadida al PDF");

      // 💾 Guardar PDF firmado
      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      console.log("💾 PDF firmado generado, tamaño:", pdfBlob.size, "bytes");

      // 📤 Subir a Drive
      const formData = new FormData();
      formData.append("file", pdfBlob, `contrato_firmado_${paciente.id}.pdf`);
      formData.append("patient_id", paciente.id);
      formData.append("type", "contrato");
      formData.append("specialty", selectedSpec);

      console.log("📦 FormData listo para enviar →", {
        patient_id: paciente.id,
        specialty: selectedSpec,
      });

      const res = await fetch("https://te-ayudo.es/back/api/upload_drive.php", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      console.log("⏳ Esperando respuesta del servidor...");
      const text = await res.text();
      console.log("📩 RAW RESPONSE FROM BACKEND:", text);

      let data;
      try {
        data = JSON.parse(text);
        console.log("✅ Parsed JSON:", data);
      } catch (err) {
        console.error("❌ JSON parse error:", err);
        alert("Respuesta no válida del servidor (ver consola).");
        return;
      }

      if (data.ok) {
        const driveUrl = data.file?.webViewLink || null;
        setUploadedFile({
          webViewLink: driveUrl,
          pdf_local: data.pdf_local || null,
        });
        setMessage("✅ Contrato firmado y subido correctamente.");

        console.log("📤 Subida exitosa →", driveUrl);

        // 💌 Enviar por correo
        const mailForm = new FormData();
        mailForm.append("patient_id", paciente.id);
        mailForm.append("specialty", selectedSpec);
        mailForm.append(
          "file",
          pdfBlob,
          `Contrato_firmado_${selectedSpec}.pdf`
        );

        console.log("📧 Enviando contrato firmado por correo...");
        const mailRes = await fetch(
          "https://te-ayudo.es/back/api/send_contract_mail.php",
          {
            method: "POST",
            body: mailForm,
          }
        );

        const mailText = await mailRes.text();
        console.log("📩 Respuesta cruda mail:", mailText);
        const mailData = JSON.parse(mailText);

        if (mailData.ok) {
          setMessage(
            "✅ Contrato firmado, subido y enviado por correo correctamente."
          );
        } else {
          setMessage(
            "⚠️ Contrato subido, pero error al enviar correo: " +
              mailData.error
          );
        }
      } else {
        throw new Error(data.error || "Error al subir contrato");
      }
    } catch (err) {
      console.error("💥 Error general en handleSave:", err);
      setMessage("❌ " + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="contrato-tab">
      <h3>🧾 Contrato de prestación de servicios</h3>

      {availableSpecs.length > 0 && (
        <div className="form-row">
          <label>Especialidad / Servicio</label>
          <select
            value={selectedSpec}
            onChange={(e) => setSelectedSpec(e.target.value)}
          >
            <option value="">-- Selecciona un servicio --</option>
            {availableSpecs.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && <p>Generando contrato personalizado...</p>}
      {error && <p style={{ color: "red" }}>❌ {error}</p>}

      {/* Vista previa + botón pantalla completa */}
      {!loading && fileUrl && (
        <>
          <div className="contrato-preview-wrapper">
            <iframe
              src={fileUrl}
              title="Vista previa contrato PDF"
              className="contrato-preview"
            ></iframe>
          </div>

          <p className="contrato-open-fullscreen-row">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-open-fullscreen"
            >
              📄 Abrir contrato a pantalla completa
            </a>
          </p>
        </>
      )}

      {fileUrl && (
        <div className="signature-section">
          <h4>✍️ Firma del paciente o tutor</h4>
          <div className="signature-box">
            <canvas
              ref={canvasRef}
              width={350}
              height={150}
              className="signature-canvas"
            />
          </div>
          <div className="signature-actions">
            <button onClick={handleClear}>Borrar firma</button>
            <button
              onClick={handleSave}
              className="primary-btn"
              disabled={uploading}
            >
              {uploading ? "Guardando..." : "Guardar contrato firmado"}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className="message-box">
          <p>{message}</p>
          {uploadedFile && (
            <button
              className="btn-ver-contrato"
              onClick={() => {
                const url = uploadedFile.pdf_local || uploadedFile.webViewLink;
                if (url) window.open(url, "_blank");
              }}
            >
              📄 Ver contrato firmado
            </button>
          )}
        </div>
      )}
    </div>
  );
}
